// Rendered instance of a time-boxed task in the calendar.
import { useDraggable } from '@dnd-kit/core';
import useTaskResize from '@/features/planning/hooks/time-grid/useTaskResize.js';
import { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import { useAuth } from '@/features/auth/useAuth';
import {
  useBreakpoint,
  isBattlefieldBreakpoint,
} from '@/hooks/ui/useBreakpoint.js';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { TIME_SLOT_MINS } from '@/config/constants';
import TaskContextMenuTarget from '@/features/tasks/components/context-menu/TaskContextMenuTarget';
import { useQuickbarPlaceholder } from '@/hooks/ui/useQuickbarPlaceholder';
import {
  DEFAULT_SCHEDULED_TASK_PROPS,
  computeBoxSx,
  computeTaskClasses,
  getPrimaryItem,
  mergePositionStyles,
  renderNewTaskPlaceholder,
} from '@/features/planning/components/time-grid/ScheduledTaskHelpers';
import { ScheduledTaskTitleRow } from '@/features/planning/components/time-grid/ScheduledTaskTitleRow';
import { renderScheduledTaskResizeHandle } from '@/features/planning/components/time-grid/ScheduledTaskChrome';
import '@/styles/task-shared.css';

/** @typedef {import('@/types').Task} Task */

const bodySx = (isShort) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  p: isShort ? 0 : '2px 4px',
  flexGrow: 1,
  minHeight: 0,
  width: '100%',
  boxSizing: 'border-box',
  justifyContent: isShort ? 'center' : 'flex-start',
});

const useScheduledTaskMeta = (task) => {
  const primaryProject = useMemo(
    () => getPrimaryItem((task.projects || []).concat(task.project || [])),
    [task.project, task.projects],
  );
  return { primaryProject };
};

/**
 * ScheduledTask - Specialized component for the time-grid in the calendar.
 * Note: This is NOT a wrapper of TaskListItem, but an independent component
 * with a distinct API (utilizes fifteenMinuteSlotHeight and useTaskResize).
 */
const ScheduledTask = (props) => {
  const fullProps = { ...DEFAULT_SCHEDULED_TASK_PROPS, ...props };
  const {
    task,
    style,
    fifteenMinuteSlotHeight,
    navigate,
    currentTaskId,
    onSave,
    onCancel,
    isOverlay,
    keyboardActive = false,
    onboardingHighlightResize = false,
  } = fullProps;

  const { handleToggleComplete, updateTaskDuration } =
    useTaskMutationsContext();
  const { user } = useAuth();
  const currentBreakpoint = useBreakpoint();
  const isBattlefield = isBattlefieldBreakpoint(currentBreakpoint);
  const { projectDisplayMap } = useTasksContext();
  const quickbarPlaceholder = useQuickbarPlaceholder({ narrowField: true });

  const isCurrent =
    currentTaskId !== null &&
    currentTaskId !== undefined &&
    task.id === currentTaskId;

  // Battlefield: drag schedule/unschedule allowed; resize stays off (touch vs scroll).
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: !!isOverlay,
    data: { task, type: 'scheduled-task' },
  });

  const { primaryProject } = useScheduledTaskMeta(task);

  const { currentHeight, handleMouseDown } = useTaskResize({
    task,
    style,
    fifteenMinuteSlotHeight,
    onResizeComplete: (taskId, newDuration) =>
      !task.isNew && updateTaskDuration(taskId, newDuration),
    disabled: isBattlefield || isOverlay,
  });

  if (task.isNew) {
    return renderNewTaskPlaceholder(
      style,
      fifteenMinuteSlotHeight,
      onSave,
      onCancel,
      quickbarPlaceholder,
    );
  }

  const isShort = task.duration <= TIME_SLOT_MINS;
  const layoutClass =
    task.totalColumns && task.totalColumns > 1 ? 'layout-column' : 'layout-row';
  const taskClassName = computeTaskClasses(
    isCurrent,
    task,
    layoutClass,
    isShort,
    isDragging,
    isOverlay,
  );
  const boxSx = {
    ...computeBoxSx(isCurrent, isDragging, isOverlay, task, isShort),
    ...(keyboardActive
      ? {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 1,
          zIndex: 5,
        }
      : {}),
  };
  const positionStyles = mergePositionStyles(currentHeight, style);
  const dragProps = { ...listeners, ...attributes, tabIndex: -1 };
  const resizeHandle = !isBattlefield
    ? renderScheduledTaskResizeHandle(
        handleMouseDown,
        onboardingHighlightResize,
      )
    : null;

  return (
    <TaskContextMenuTarget task={task} surface="plan-grid">
      <Box
        ref={setNodeRef}
        {...dragProps}
        className={taskClassName}
        sx={{
          ...boxSx,
          ...positionStyles,
        }}
        id={`calendar-scheduled-task-${task.id}`}
        role="option"
        aria-label={task.title}
        aria-selected={keyboardActive}
        data-task-id={task.id}
        data-testid={`scheduled-task-${task.id}`}
        data-scheduled-task="true"
        data-compact={isShort || undefined}
        onDoubleClick={() =>
          navigate(`${window.location.pathname}?task=${task.id}`)
        }
      >
        <Box sx={bodySx(isShort)}>
          <ScheduledTaskTitleRow
            task={task}
            user={user}
            handleToggleComplete={handleToggleComplete}
            isShort={isShort}
            primaryProject={primaryProject}
            projectDisplayMap={projectDisplayMap}
          />
        </Box>
        {resizeHandle}
      </Box>
    </TaskContextMenuTarget>
  );
};

export default memo(ScheduledTask);
